import Phaser from "phaser";
import { isArtLayoutCapture } from "../../artLayout";
import { BOARD_TEXTURES, environmentTexture } from "../assets";
import type { Board, GridPos } from "../model/board";
import type { Side } from "../model/battle";
import { PieceView } from "./PieceView";
import { boardLayout, project, type IsoLayout } from "./iso";
import { STAGE, stageX, stageY } from "./stage";

export function drawDiamond(g: Phaser.GameObjects.Graphics, x: number, y: number, width: number, height: number): void {
  g.beginPath().moveTo(x, y - height / 2).lineTo(x + width / 2, y)
    .lineTo(x, y + height / 2).lineTo(x - width / 2, y).closePath();
}

const sceneLayouts = new WeakMap<Phaser.Scene, Record<Side, IsoLayout>>();
export const layoutFor = (scene: Phaser.Scene, side: Side): IsoLayout => sceneLayouts.get(scene)![side];
export const pieceDepth = (pos: GridPos): number => 30 + (pos.x + pos.y) * 10 + pos.x;

export function drawBattlefield(scene: Phaser.Scene, level: number): void {
  // All drawing, clicks and animations share this scene's painted board positions.
  sceneLayouts.set(scene, { player: boardLayout("player", level), opponent: boardLayout("opponent", level) });
  if (!isArtLayoutCapture) {
    scene.cameras.main.setBackgroundColor("#65818a");
    scene.add.image(STAGE.width / 2, STAGE.height / 2, environmentTexture(level)).setDisplaySize(STAGE.width, STAGE.height).setDepth(-10);
    // A quiet glaze keeps the active stone planes readable against the painting.
    scene.add.rectangle(STAGE.width / 2, STAGE.height / 2, STAGE.width, STAGE.height, 0x132f35, 0.1).setDepth(-9);
  }
  drawGrid(scene, layoutFor(scene, "player"));
  drawGrid(scene, layoutFor(scene, "opponent"));
  const badge = scene.add.graphics().setDepth(600).setScale(stageX(1), stageY(1));
  badge.fillStyle(0x213b34,0.94).fillRoundedRect(32,28,94,78,22);
  badge.lineStyle(3,0xb9a57c).strokeRoundedRect(32,28,94,78,22);
  text(scene,79,66,`${level}`,38).setOrigin(0.5);
}

function drawGrid(scene: Phaser.Scene, layout: IsoLayout): void {
  const seams = scene.add.graphics().setDepth(2);
  const { tileWidth: w, tileHeight: h } = layout;
  // Soft seams keep cells readable without competing with the painted pieces.
  if (isArtLayoutCapture) for (let diagonal = 0; diagonal <= 22; diagonal++) {
    for (let y = 0; y < 12; y++) {
      const x = diagonal - y;
      if (x < 0 || x > 11) continue;
      const p = project({ x, y }, layout);
      scene.add.image(p.x, p.y, BOARD_TEXTURES.tile).setDisplaySize(w - 0.6, h - 0.3).setDepth(1).setAlpha(0.87)
        .setTint([0xffffff, 0xf5eddd, 0xece9d7, 0xf5f0df][(x * 19 + y * 23) % 4]);
    }
  }
  // Draw shared edges once, so intersections don't become heavy doubled outlines.
  seams.lineStyle(isArtLayoutCapture ? 0.8 : 1.7, isArtLayoutCapture ? 0x6f705b : 0xf2d795, isArtLayoutCapture ? 0.28 : 0.57);
  for (let i = 0; i <= 12; i++) {
    const a = project({ x: i - 0.5, y: -0.5 }, layout), b = project({ x: i - 0.5, y: 11.5 }, layout);
    const c = project({ x: -0.5, y: i - 0.5 }, layout), d = project({ x: 11.5, y: i - 0.5 }, layout);
    seams.lineBetween(a.x, a.y, b.x, b.y).lineBetween(c.x, c.y, d.x, d.y);
  }
}

export function makeTeam(scene: Phaser.Scene, board: Board, side: Side, health = false, motion = true): Map<string, PieceView> {
  const views = new Map<string, PieceView>();
  board.cells.forEach((piece, index) => {
    if (!piece) return;
    const pos = { x: index % board.width, y: Math.floor(index / board.width) };
    const point = project(pos, layoutFor(scene, side));
    const view = new PieceView(scene, piece, point.x, point.y, pieceDepth(pos), health, motion);
    view.face(side === "player" ? -1 : 1);
    views.set(piece.id, view);
  });
  return views;
}

export function text(scene: Phaser.Scene, x: number, y: number, value: string, size = 25, color = "#fff0cf"): Phaser.GameObjects.Text {
  return scene.add.text(stageX(x), stageY(y), value, { fontFamily: "Georgia, serif", fontSize: `${size}px`, color }).setDepth(610);
}
